import { MissingBaseUrlError, NotAuthenticatedError } from '../errors';
import type {
  EmitGameEventResponse,
  GameAssetDto,
  GameEventKey,
  GameOwnedAssetDto,
  GameStoreDto,
  GameStoreOfferPurchaseResultDto,
  PagingResponse,
  RecordGameplayActionRequest,
} from './LooplaySDK.types';
import { ServiceClient, type TrackingAuth } from './service-client';
import type { ServiceClientOptions } from './service-client';

function generateId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  // Fallback UUID v4 for environments without crypto.randomUUID. Not
  // cryptographically strong, but only used as a non-sensitive tracking id.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

interface CachedTrackingSession {
  trackingSessionId: string;
  anonTrackingToken: string;
  expiresAtMs: number;
}

/** Signed `playSessionToken` for the current play session — required on every play/match/action call. */
interface CachedPlaySession {
  gameKey: string;
  playSessionId: string;
  playSessionToken: string;
  expiresAtMs: number;
}

export interface ApiClientOptions extends Omit<ServiceClientOptions, 'baseUrl'> {
  baseUrl?: string;
  getAccessToken?: () => Promise<string | undefined>;
  /**
   * Resolves an anonymous device id, used for the tracking endpoints
   * (`trackPlay`/`trackMatch`/`emit`) when `getAccessToken` yields nothing.
   * Anonymous tracking is analytics-only — see `TrackingAuth`.
   */
  getAnonymousId?: () => string | undefined;
}

/**
 * High-level API client that injects user auth automatically.
 */
export class ApiClient {
  private readonly raw: ServiceClient;
  private readonly getAccessToken?: () => Promise<string | undefined>;
  private readonly getAnonymousId?: () => string | undefined;
  private trackingSession?: CachedTrackingSession;
  private trackingSessionPromise?: Promise<CachedTrackingSession | undefined>;
  private currentPlaySessionId?: string;
  private playSession?: CachedPlaySession;
  private playSessionPromise?: Promise<CachedPlaySession>;

  constructor(options: ApiClientOptions) {
    if (!options.baseUrl) throw new MissingBaseUrlError();

    this.raw = new ServiceClient({
      baseUrl: options.baseUrl,
      fetch: options.fetch,
      defaultHeaders: options.defaultHeaders,
    });

    this.getAccessToken = options.getAccessToken;
    this.getAnonymousId = options.getAnonymousId;
  }

  /** Expose the underlying route-level client (requires manual bearerToken/gameKey passing). */
  unsafeRaw(): ServiceClient {
    return this.raw;
  }

  async detectIntegration(gameKey: string): Promise<boolean> {
    return this.raw.detectGameIntegration(gameKey);
  }

  // Real-time (balance_change / store_purchase_completed WebSocket events)
  // deliberately lives outside this class — import `WsClient` from
  // '@looplay/sdk/realtime' and construct it with the same `getAccessToken`
  // you passed here. Keeps the socket.io-client dependency (and its bundle
  // weight) out of every consumer that doesn't need real-time updates.

  private async requireToken(): Promise<string> {
    const token = await this.getAccessToken?.();
    if (!token) throw new NotAuthenticatedError();
    return token;
  }

  /**
   * Resolves auth for the tracking endpoints: a real bearer token if logged
   * in, otherwise an anonymous device id. Throws only when neither is
   * available — anonymous tracking still requires *some* identity. Also
   * attaches a `trackingSessionId`/`anonTrackingToken` (bootstrapped and
   * cached lazily) — the backend's tracking contract expects these even for
   * logged-in callers, for unified tracing.
   */
  private async resolveTrackingAuth(): Promise<TrackingAuth> {
    const bearerToken = await this.getAccessToken?.();
    const anonId = bearerToken ? undefined : this.getAnonymousId?.();

    if (!bearerToken && !anonId) throw new NotAuthenticatedError();

    const session = await this.ensureTrackingSession(anonId);

    return {
      bearerToken,
      anonId,
      trackingSessionId: session?.trackingSessionId,
      anonTrackingToken: session?.anonTrackingToken,
    };
  }

  /**
   * Bootstraps (and caches until near expiry) the platform tracking
   * session. Swallows failures — tracking calls still work without it,
   * just without unified session tracing.
   */
  private async ensureTrackingSession(
    anonId?: string
  ): Promise<CachedTrackingSession | undefined> {
    const now = Date.now();
    if (this.trackingSession && this.trackingSession.expiresAtMs > now + 30_000) {
      return this.trackingSession;
    }

    if (!this.trackingSessionPromise) {
      this.trackingSessionPromise = this.raw
        .bootstrapTrackingSession(anonId)
        .then((dto) => {
          this.trackingSession = {
            trackingSessionId: dto.trackingSessionId,
            anonTrackingToken: dto.trackingToken,
            expiresAtMs: new Date(dto.expiresAt).getTime(),
          };
          return this.trackingSession;
        })
        .catch(() => undefined)
        .finally(() => {
          this.trackingSessionPromise = undefined;
        });
    }

    return this.trackingSessionPromise;
  }

  /**
   * Signs (and caches until near expiry) the `playSessionToken` required by
   * `trackPlay`/`trackMatch`/`emit` for the current play session — does not
   * swallow failures, unlike `ensureTrackingSession`, since these calls
   * are rejected outright without a valid token.
   */
  private async ensurePlaySession(
    gameKey: string,
    auth: TrackingAuth,
    playSessionId: string
  ): Promise<CachedPlaySession> {
    const now = Date.now();
    if (
      this.playSession &&
      this.playSession.gameKey === gameKey &&
      this.playSession.playSessionId === playSessionId &&
      this.playSession.expiresAtMs > now + 10_000
    ) {
      return this.playSession;
    }

    if (!this.playSessionPromise) {
      this.playSessionPromise = this.raw
        .startPlaySession(gameKey, auth, { playSessionId })
        .then((dto) => {
          this.playSession = {
            gameKey,
            playSessionId: dto.playSessionId,
            playSessionToken: dto.playSessionToken,
            expiresAtMs: new Date(dto.expiresAt).getTime(),
          };
          return this.playSession;
        })
        .finally(() => {
          this.playSessionPromise = undefined;
        });
    }

    return this.playSessionPromise;
  }

  /**
   * Call once when the game view opens, before any play/match tracking.
   * `gameKey` is the game's public `appId`.
   */
  async trackView(gameKey: string): Promise<boolean> {
    const auth = await this.resolveTrackingAuth();
    return this.raw.recordGameView(gameKey, auth);
  }

  /**
   * Starts a new play session and returns its id. Call when the user
   * presses Play; `trackPlay`/`trackMatch`/`emit` reuse this id (and the
   * signed `playSessionToken` backing it) across calls until the next
   * `startPlaySession()`, auto-starting one on first use if none was started.
   */
  startPlaySession(): string {
    this.currentPlaySessionId = generateId();
    this.playSession = undefined;
    return this.currentPlaySessionId;
  }

  async trackPlay(gameKey: string, playTimeSeconds?: number): Promise<boolean> {
    const auth = await this.resolveTrackingAuth();
    const playSessionId = this.currentPlaySessionId ?? this.startPlaySession();
    const { playSessionToken } = await this.ensurePlaySession(gameKey, auth, playSessionId);
    return this.raw.completePlaySession(gameKey, auth, { playTimeSeconds, playSessionId, playSessionToken });
  }

  async trackMatch(
    gameKey: string,
    body: { matchId: string; matchDurationSeconds: number; isCompleted?: boolean; isWin?: boolean }
  ): Promise<boolean> {
    const auth = await this.resolveTrackingAuth();
    const playSessionId = this.currentPlaySessionId ?? this.startPlaySession();
    const { playSessionToken } = await this.ensurePlaySession(gameKey, auth, playSessionId);
    return this.raw.completeGameplayMatch(gameKey, auth, { ...body, playSessionId, playSessionToken });
  }

  async emit(
    gameKey: string,
    actionCode: GameEventKey | string,
    opts?: { value?: number; refId?: string; payload?: Record<string, unknown> }
  ): Promise<EmitGameEventResponse> {
    const auth = await this.resolveTrackingAuth();
    const playSessionId = this.currentPlaySessionId ?? this.startPlaySession();
    const { playSessionToken } = await this.ensurePlaySession(gameKey, auth, playSessionId);
    const body: RecordGameplayActionRequest = {
      playSessionId,
      playSessionToken,
      actionCode,
      value: opts?.value,
      refId: opts?.refId,
      payload: opts?.payload,
    };
    return this.raw.recordGameplayAction(gameKey, auth, body);
  }

  /** Fetches the storefront (sections + offers) for the integration's game. Read-only, no auth required. */
  async getStore(gameKey: string): Promise<GameStoreDto> {
    return this.raw.getStore(gameKey);
  }

  /** Public catalog of purchasable/ownable assets for the game. Read-only, no auth required. */
  async listGameAssets(gameKey: string): Promise<GameAssetDto[]> {
    return this.raw.listGameAssets(gameKey);
  }

  /** Assets the authenticated player already owns for this game. */
  async listMyGameAssets(gameKey: string): Promise<GameOwnedAssetDto[]> {
    const token = await this.requireToken();
    return this.raw.listMyGameAssets(token, gameKey);
  }

  /** Purchase history for the authenticated player in the game's store. */
  async listMyStorePurchases(gameKey: string): Promise<GameStoreOfferPurchaseResultDto[]> {
    const token = await this.requireToken();
    return this.raw.listMyStorePurchases(token, gameKey);
  }

  /**
   * Spends coin balance to purchase a store offer — signs a checkout intent
   * first, then redeems it, mirroring the play session flow above.
   * Self-serve: only the player's own access token is used, no secret pair
   * required, safe to call directly from the game client. Also see
   * `onStorePurchase` to react to the result in real time from any tab.
   */
  async purchaseStoreOffer(
    gameKey: string,
    offerCode: string,
    opts?: { requestId?: string; quantity?: number }
  ): Promise<GameStoreOfferPurchaseResultDto> {
    const token = await this.requireToken();
    const intent = await this.raw.createMyStoreCheckoutIntent(gameKey, token, {
      offerCode,
      requestId: opts?.requestId,
      quantity: opts?.quantity,
    });
    return this.raw.purchaseMyStoreOffer(gameKey, token, {
      offerCode: intent.offerCode,
      requestId: intent.requestId,
      quantity: intent.quantity,
      checkoutToken: intent.checkoutToken,
    });
  }

  async getMyProfile(): Promise<any> {
    const token = await this.requireToken();
    return this.raw.getMyProfile(token);
  }

  async getMyBalance(): Promise<any> {
    const token = await this.requireToken();
    return this.raw.getMyBalance(token);
  }

  async getBalances(query?: Record<string, any>): Promise<any> {
    const token = await this.requireToken();
    return this.raw.getBalances(token, query);
  }

  async getBalanceHistory(query?: Record<string, any>): Promise<PagingResponse<any>> {
    const token = await this.requireToken();
    return this.raw.getBalanceHistory(token, query);
  }
}
